/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useMemo, useState, type ReactNode } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import { Box, Icon, InputAdornment, TextField, useTheme } from '@mui/material';
import { debounce } from 'lodash';

import type { SearchFieldProps } from './types';

export function SearchField( { onSearch, placeholder, label }: SearchFieldProps ): ReactNode {
	const { palette } = useTheme();
	const [ searchQuery, setSearchQuery ] = useState( '' );

	// Wrapped inside useMemo() to avoid re-creating debounced search function each time the component is re-rendered.
	const debouncedSearch = useMemo( () => debounce( onSearch, 500 ), [ onSearch ] );

	function handleSearch() {
		return ( event: React.ChangeEvent<HTMLInputElement> ) => {
			const searchQuery = event.target.value;

			setSearchQuery( searchQuery );
			debouncedSearch( searchQuery );
		};
	}

	function clearSearchQuery() {
		setSearchQuery( '' );

		// Clear the search results immediately.
		debouncedSearch( '' );
		debouncedSearch.flush();
	}

	return (
		<Box sx={ { display: 'flex', gap: '20px', flexFlow: 'column' } }>
			<TextField
				data-testid={ 'search-field' }
				fullWidth={ true }
				size='small'
				placeholder={ placeholder }
				label={ label }
				onChange={ handleSearch() }
				value={ searchQuery }
				inputProps={ {
					style: {
						padding: '8px 12px 8px 0'
					}
				} }
				InputProps={ {
					startAdornment: (
						<InputAdornment position='start'>
							<Icon sx={ { lineHeight: 0, width: '20px', height: '20px', color: palette.text.secondary } }>
								<SearchIcon sx={ { width: '20px', height: '20px' } }/>
							</Icon>
						</InputAdornment>
					),
					endAdornment: ( searchQuery &&
						<InputAdornment position='end'>
							<Icon
								sx={ { lineHeight: 0, width: '20px', height: '20px', color: palette.secondary.main, cursor: 'pointer' } }
								onClick={ clearSearchQuery }
								data-testid='clear-search-query'
							>
								<CancelIcon sx={ { width: '20px', height: '20px' } }/>
							</Icon>
						</InputAdornment>
					)
				} }
				sx={ {
					borderRadius: '4px',
					border: `1px solid ${ palette.secondary[ 100 ] }`
				} }
			/>
		</Box>
	);
}
